/**
 * Printable sales-order documents: pick slip, packing slip, tax invoice.
 * Read-only projection. Printing must not write orders, inventory, fulfilment,
 * tracking, or marketplace state.
 */

import { money, fmtDate, variantLabel } from "@/lib/format";
import { provenanceLabel, buildProvenance, type ProvenanceInput } from "@/lib/order-provenance";
import { paymentStatusLabel } from "@/lib/sales-orders";
import { canonicalLineSku, pickSlicesFromReservations, type AllocationLedgerEntry, type PickLineInput } from "@/lib/warehouse-projection";
import { chooseTaxInclusive, composeCanonicalOrderFinancials } from "@/lib/providers/order-financials";
import { formatAddress } from "@/lib/shipping";
import type { OrgBusinessIdentity } from "@/lib/org-business-identity";
import { canWorkSales } from "@/lib/permissions";
import { operatorPreviewStatus } from "@/lib/listing-media-preview";
import { barcodePrintHtml } from "@/lib/document-barcode";
import { postalAddressFromRecord, postalAddressHasContent, type PostalAddressBlock } from "@/lib/postal-address";

export type OrderDocumentKind = "pick" | "pack" | "invoice";

export const ORDER_DOCUMENT_WRITES = Object.freeze({
  database: false,
  inventory: false,
  fulfilment: false,
  tracking: false,
  marketplace: false,
  provider: false,
});

export function printDocumentsCauseNoWrites(): typeof ORDER_DOCUMENT_WRITES {
  return ORDER_DOCUMENT_WRITES;
}

export function canPrintOrderDocuments(role: string | null | undefined): boolean {
  return canWorkSales(role);
}

export function qtyToPick(qtyOrdered: number, qtyFulfilled: number): number {
  return Math.max(0, Number(qtyOrdered || 0) - Number(qtyFulfilled || 0));
}

export function formatOptionPairs(optionValues: Record<string, unknown> | null | undefined): string {
  if (!optionValues || typeof optionValues !== "object") return "";
  return Object.entries(optionValues)
    .map(([k, v]) => {
      const val = String(v ?? "").trim();
      const key = String(k ?? "").trim();
      if (!val) return "";
      return key ? `${key}: ${val}` : val;
    })
    .filter(Boolean)
    .join(" · ");
}

export function customerFacingNote(notes: string | null | undefined): string | null {
  const t = (notes ?? "").trim();
  if (!t) return null;
  if (/^imported from\b/i.test(t)) return null;
  return t;
}

export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const SECRET_KEY = /token|secret|password|credential|api[_-]?key/i;

export function documentContainsForbiddenSecrets(html: string): boolean {
  const withoutImages = html.replace(/<img\b[^>]*>/gi, "");
  return SECRET_KEY.test(withoutImages);
}

export function canonicalCustomerPhone(
  shippingAddress: Record<string, unknown> | null | undefined,
  customerPhone?: string | null,
): string | null {
  const rec = shippingAddress && typeof shippingAddress === "object" ? shippingAddress : null;
  const fromShip = rec ? String(rec.phone ?? "").trim() : "";
  if (fromShip) return fromShip;
  const fromCustomer = (customerPhone ?? "").trim();
  return fromCustomer || null;
}

export function documentFulfilmentLocation(
  orderLocation: string | null | undefined,
  pickLocations: Array<string | null | undefined> = [],
): string {
  const named = (orderLocation ?? "").trim();
  if (named) return named;
  const fromLines = [...new Set(pickLocations.map((s) => (s ?? "").trim()).filter(Boolean))];
  return fromLines.join(" · ") || "—";
}

export type CanonicalLineImageChoice = {
  source: "variant" | "product" | null;
  storageKey: string | null;
};

export function chooseCanonicalLineImage(
  variantStorageKey: string | null | undefined,
  productStorageKey: string | null | undefined,
): CanonicalLineImageChoice {
  const variant = (variantStorageKey ?? "").trim();
  if (variant) return { source: "variant", storageKey: variant };
  const product = (productStorageKey ?? "").trim();
  if (product) return { source: "product", storageKey: product };
  return { source: null, storageKey: null };
}

export function printableMediaSrc(
  storageKey: string | null | undefined,
  signedByKey: Record<string, string>,
): string | null {
  const status = operatorPreviewStatus(storageKey ?? null, signedByKey);
  if (status.kind !== "ready" || !status.src) return null;
  if (!/^https?:\/\//i.test(status.src)) return null;
  return status.src;
}

function thumbCell(url: string | null | undefined): string {
  const src = (url ?? "").trim();
  if (/^https?:\/\//i.test(src)) {
    return `<td class="thumb"><img class="line-thumb" src="${escapeHtml(src)}" alt="" /></td>`;
  }
  return `<td class="thumb"><div class="thumb-empty" aria-hidden="true"></div></td>`;
}

export type StoredOrderMoney = {
  subtotal: number | null;
  tax: number | null;
  shipping: number | null;
  discount: number | null;
  total: number;
  currency: string | null;
};

export type InvoiceMoneyView = {
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  grandTotal: number;
  currency: string;
  taxInclusive: boolean;
  taxCaption: string;
};

/** Display stored provider financials. Never add GST on top of an inclusive total. */
export function invoiceMoneyFromStored(order: StoredOrderMoney): InvoiceMoneyView {
  const subtotal = Number(order.subtotal ?? 0);
  const tax = Number(order.tax ?? 0);
  const shipping = Number(order.shipping ?? 0);
  const discount = Number(order.discount ?? 0);
  const providerTotal = Number(order.total ?? 0);
  const exclusiveComposed = subtotal + shipping + tax - discount;
  const inclusiveComposed = subtotal + shipping - discount;
  const taxInclusive = chooseTaxInclusive({ providerTotal, exclusiveComposed, inclusiveComposed });
  const composed = composeCanonicalOrderFinancials({
    subtotal,
    tax,
    shipping,
    discount,
    taxInclusive,
    providerTotal,
    currency: order.currency ?? "AUD",
  });
  return {
    subtotal: composed.subtotal,
    tax: composed.tax,
    shipping: composed.shipping,
    discount: composed.discount,
    grandTotal: composed.total,
    currency: composed.currency,
    taxInclusive: composed.taxInclusive,
    taxCaption: composed.taxInclusive ? "GST (included)" : "GST",
  };
}

export type ShippingLabelPrintState =
  | { available: true; url: string; label: "View & print shipping label" }
  | { available: false; url: null; label: "No shipping label purchased in Tradivo" };

export function shippingLabelPrintState(shipments: Array<{ label_url?: string | null; carrier_reference?: string | null; tracking_number?: string | null }>): ShippingLabelPrintState {
  const withUrl = shipments.find((s) => {
    const url = (s.label_url ?? "").trim();
    return /^https?:\/\//i.test(url);
  });
  if (withUrl?.label_url) {
    return { available: true, url: withUrl.label_url.trim(), label: "View & print shipping label" };
  }
  const trackingOnly = shipments.some((s) => Boolean((s.tracking_number ?? "").trim()) && !(s.carrier_reference || s.label_url));
  if (trackingOnly || shipments.length > 0) {
    return { available: false, url: null, label: "No shipping label purchased in Tradivo" };
  }
  return { available: false, url: null, label: "No shipping label purchased in Tradivo" };
}

export type OrderDocumentLine = {
  id: string;
  productName: string;
  options: string;
  sku: string;
  barcode: string | null;
  qtyOrdered: number;
  qtyFulfilled: number;
  qtyToPick: number;
  qtyPacked: number;
  unitPrice: number;
  lineTotal: number;
  pickLocation: string | null;
  imageUrl: string | null;
};

export type OrderDocumentModel = {
  orgId: string;
  orderId: string;
  orderNumber: string;
  orderDate: string;
  channel: string;
  customerName: string;
  customerPhone: string | null;
  fulfilmentLocation: string;
  shipTo: string;
  billTo: string;
  shipToPostal: PostalAddressBlock | null;
  billToPostal: PostalAddressBlock | null;
  payment: string;
  shippingMethod: string;
  notes: string | null;
  customerReference: string | null;
  currency: string;
  money: InvoiceMoneyView;
  lines: OrderDocumentLine[];
  identity: OrgBusinessIdentity;
};

export type OrderDocumentSource = {
  orgId: string;
  orderId: string;
  orderNumber: string;
  orderDate: string | null;
  source: string;
  connectorProvider?: string | null;
  connectorName?: string | null;
  externalOrderId?: string | null;
  customerName: string | null;
  customerPhone?: string | null;
  fulfilmentLocation: string | null;
  shippingAddress: Record<string, unknown> | null;
  billingAddress: Record<string, unknown> | null;
  paymentStatus: string | null;
  notes: string | null;
  customerReference: string | null;
  shippingMethod: string | null;
  money: StoredOrderMoney;
  lines: Array<{
    id: string;
    salesOrderId: string;
    productId?: string | null;
    variantId: string | null;
    productName: string | null;
    imageUrl?: string | null;
    optionValues: Record<string, unknown> | null;
    sku: string | null;
    variantSku: string | null;
    productSku: string | null;
    barcode: string | null;
    qtyOrdered: number;
    qtyAllocated: number;
    qtyFulfilled: number;
    unitPrice: number;
    lineTotal: number;
  }>;
  ledger: AllocationLedgerEntry[];
  identity: OrgBusinessIdentity;
};

export function buildOrderDocumentModel(src: OrderDocumentSource): OrderDocumentModel {
  const pickLines: PickLineInput[] = src.lines.map((l) => ({
    id: l.id,
    sales_order_id: l.salesOrderId,
    variant_id: l.variantId,
    qty_allocated: l.qtyAllocated,
  }));
  const slices = pickSlicesFromReservations({ lines: pickLines, ledger: src.ledger });
  const locByLine = new Map<string, string>();
  for (const slice of slices) {
    const name = (slice.locationName ?? "").trim();
    if (!name) continue;
    const prev = locByLine.get(slice.line.id);
    locByLine.set(slice.line.id, prev && prev !== name ? `${prev} · ${name}` : name);
  }

  const provenance = buildProvenance({
    source: src.source,
    connectorProvider: src.connectorProvider,
    connectorName: src.connectorName,
    externalOrderId: src.externalOrderId,
  } as ProvenanceInput);

  const lineModels = src.lines.map((l) => {
    const sku = canonicalLineSku({ lineSku: l.sku, variantSku: l.variantSku, productSku: l.productSku }) ?? "—";
    const options = formatOptionPairs(l.optionValues) || variantLabel(l.optionValues);
    return {
      id: l.id,
      productName: (l.productName ?? "").trim() || "—",
      options,
      sku,
      barcode: (l.barcode ?? "").trim() || null,
      qtyOrdered: l.qtyOrdered,
      qtyFulfilled: l.qtyFulfilled,
      qtyToPick: qtyToPick(l.qtyOrdered, l.qtyFulfilled),
      qtyPacked: l.qtyFulfilled > 0 ? l.qtyFulfilled : l.qtyOrdered,
      unitPrice: l.unitPrice,
      lineTotal: l.lineTotal,
      pickLocation: locByLine.get(l.id) ?? (l.qtyAllocated > 0 ? src.fulfilmentLocation : null),
      imageUrl: (l.imageUrl ?? "").trim() || null,
    };
  });

  return {
    orgId: src.orgId,
    orderId: src.orderId,
    orderNumber: src.orderNumber,
    orderDate: fmtDate(src.orderDate),
    channel: provenanceLabel(provenance),
    customerName: (src.customerName ?? "").trim() || "—",
    customerPhone: canonicalCustomerPhone(src.shippingAddress, src.customerPhone),
    fulfilmentLocation: documentFulfilmentLocation(
      src.fulfilmentLocation,
      lineModels.map((l) => l.pickLocation),
    ),
    shipTo: formatAddress(src.shippingAddress),
    billTo: formatAddress(src.billingAddress),
    shipToPostal: postalAddressFromRecord(src.shippingAddress),
    billToPostal: postalAddressFromRecord(src.billingAddress),
    payment: paymentStatusLabel(src.paymentStatus),
    shippingMethod: (src.shippingMethod ?? "").trim() || "—",
    notes: customerFacingNote(src.notes),
    customerReference: (src.customerReference ?? "").trim() || null,
    currency: src.money.currency || "AUD",
    money: invoiceMoneyFromStored(src.money),
    identity: src.identity,
    lines: lineModels,
  };
}

function kindTitle(kind: OrderDocumentKind): string {
  if (kind === "pick") return "Pick slip";
  if (kind === "pack") return "Packing slip";
  return "Tax invoice";
}

function formatPrintedAt(printedAt: Date): string {
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(printedAt);
}

function printCss(kind: OrderDocumentKind, footerLeft: string): string {
  const thumbs =
    kind === "invoice"
      ? ""
      : `
    td.thumb, th.thumb { width: 62px; text-align: center; vertical-align: middle; }
    img.line-thumb { width: 52px; height: 52px; object-fit: contain; border: 1px solid #d6d3d1; background: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .thumb-empty { width: 52px; height: 52px; border: 1px dashed #d6d3d1; background: #fafaf9; display: inline-block; }`;
  return `
    @page {
      size: A4 portrait;
      margin: 12mm 12mm 16mm 12mm;
      @bottom-left {
        content: ${JSON.stringify(footerLeft)};
        font-family: "Segoe UI", Arial, Helvetica, sans-serif;
        font-size: 8pt;
        color: #57534e;
      }
      @bottom-right {
        content: "Page " counter(page) " of " counter(pages);
        font-family: "Segoe UI", Arial, Helvetica, sans-serif;
        font-size: 8pt;
        color: #57534e;
      }
    }
    html, body { background: #fff; color: #1c1917; }
    body { font-family: "Segoe UI", Arial, Helvetica, sans-serif; font-size: 11px; line-height: 1.4; margin: 0; }
    .order-doc { page-break-after: always; break-after: page; padding-bottom: 8mm; }
    .order-doc:last-child { page-break-after: auto; break-after: auto; }
    .brand-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; border-bottom: 2px solid #1c1917; padding-bottom: 10px; }
    .brand-left { min-width: 0; }
    img.seller-logo { max-height: 56px; max-width: 220px; object-fit: contain; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .wordmark { font-size: 20px; font-weight: 800; letter-spacing: 0.02em; line-height: 1.1; }
    .seller-contact { text-align: right; font-size: 11px; color: #44403c; max-width: 46%; }
    .seller-contact .legal { font-weight: 700; color: #1c1917; font-size: 13px; }
    .seller-contact div { margin: 0 0 1px; }
    h1.doc-title { font-size: 28px; margin: 14px 0 12px; letter-spacing: 0.12em; font-weight: 800; }
    table.info-panel { width: 100%; border-collapse: collapse; margin: 0 0 14px; }
    table.info-panel th, table.info-panel td { border: 1px solid #d6d3d1; padding: 7px 10px; vertical-align: top; text-align: left; }
    table.info-panel th { width: 18%; background: #f5f5f4; color: #57534e; font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
    table.info-panel td { width: 32%; font-size: 12px; font-weight: 600; }
    .loc-cell { background: #fafaf9; }
    .deliver-cell { background: #fff; }
    .postal { font-weight: 600; line-height: 1.45; }
    .postal div { margin: 0; }
    .phone-value { font-size: 13px; font-weight: 800; }
    table.lines { width: 100%; border-collapse: collapse; margin-top: 2px; }
    table.lines th, table.lines td { border: 1px solid #a8a29e; padding: 7px 8px; text-align: left; vertical-align: top; }
    table.lines th { background: #1c1917; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; }
    td.wrap { word-break: break-word; overflow-wrap: anywhere; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    td.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 10px; }
    .options { color: #44403c; font-weight: 500; margin-top: 2px; }
    ${thumbs}
    .barcode { text-align: center; }
    .barcode-svg { display: block; margin: 0 auto 2px; max-width: 140px; height: 28px; }
    .barcode-text { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 9px; letter-spacing: 0.04em; }
    .barcode-none { color: #78716c; }
    .tick { width: 18px; height: 18px; border: 2px solid #1c1917; display: inline-block; vertical-align: middle; }
    .totals { width: 280px; margin-left: auto; margin-top: 12px; }
    .totals div { display: flex; justify-content: space-between; padding: 3px 0; }
    .totals .grand { font-size: 15px; font-weight: 700; border-top: 2px solid #1c1917; margin-top: 4px; padding-top: 6px; }
    .notes { margin-top: 14px; border: 1px solid #d6d3d1; padding: 8px 10px; }
    .notes .label { color: #57534e; font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 4px; }
    .notes p { margin: 0; }
    .sheet-footer { margin-top: 16px; color: #57534e; font-size: 9px; border-top: 1px solid #e7e5e4; padding-top: 6px; display: flex; justify-content: space-between; }
    @media print {
      .sheet-footer { display: none; }
    }
  `;
}

function sellerLogoHtml(id: OrgBusinessIdentity): string {
  const src = (id.logoUrl ?? "").trim();
  if (/^https?:\/\//i.test(src)) {
    return `<img class="seller-logo" src="${escapeHtml(src)}" alt="" />`;
  }
  return `<div class="wordmark">${escapeHtml(id.legalName || "—")}</div>`;
}

function sellerContactHtml(id: OrgBusinessIdentity, includeLegalName: boolean): string {
  const bits = [
    includeLegalName ? `<div class="legal">${escapeHtml(id.legalName || "—")}</div>` : "",
    id.abn ? `<div>ABN ${escapeHtml(id.abn)}</div>` : "",
    id.address ? `<div>${escapeHtml(id.address).replace(/\n/g, "<br />")}</div>` : "",
    id.phone ? `<div>${escapeHtml(id.phone)}</div>` : "",
    id.email ? `<div>${escapeHtml(id.email)}</div>` : "",
  ];
  return `<div class="seller-contact">${bits.join("")}</div>`;
}

function hasSellerLogo(id: OrgBusinessIdentity): boolean {
  return /^https?:\/\//i.test((id.logoUrl ?? "").trim());
}

function brandHeader(id: OrgBusinessIdentity): string {
  const logo = hasSellerLogo(id);
  return `<header class="brand-row">
    <div class="brand-left">${sellerLogoHtml(id)}</div>
    ${sellerContactHtml(id, logo)}
  </header>`;
}

function postalHtml(block: PostalAddressBlock | null): string {
  if (!postalAddressHasContent(block) || !block) return "—";
  const lines = [block.recipient, block.company, block.line1, block.line2, block.locality, block.country].filter(Boolean);
  return `<div class="postal">${lines.map((line) => `<div>${escapeHtml(line)}</div>`).join("")}</div>`;
}

function notesBlock(doc: OrderDocumentModel): string {
  const parts = [
    doc.notes ? `<p>${escapeHtml(doc.notes)}</p>` : "",
    doc.customerReference ? `<p>Customer reference: ${escapeHtml(doc.customerReference)}</p>` : "",
  ].filter(Boolean);
  if (parts.length === 0) return `<div class="notes"><p class="label">Notes</p><p>—</p></div>`;
  return `<div class="notes"><p class="label">Notes</p>${parts.join("")}</div>`;
}

function sheetFooter(footerLeft: string): string {
  return `<div class="sheet-footer"><span>${escapeHtml(footerLeft)}</span><span></span></div>`;
}

function productCell(l: OrderDocumentLine): string {
  return `<td class="wrap">${escapeHtml(l.productName)}${l.options ? `<div class="options">${escapeHtml(l.options)}</div>` : ""}</td>`;
}

function renderPick(doc: OrderDocumentModel, footerLeft: string): string {
  const rows = doc.lines
    .map(
      (l) => `<tr>
      ${thumbCell(l.imageUrl)}
      ${productCell(l)}
      <td class="mono wrap">${escapeHtml(l.sku)}</td>
      <td>${barcodePrintHtml(l.barcode, l.sku)}</td>
      <td class="num">${escapeHtml(l.qtyOrdered)}</td>
      <td class="num">${escapeHtml(l.qtyFulfilled)}</td>
      <td class="num"><strong>${escapeHtml(l.qtyToPick)}</strong></td>
      <td class="wrap">${escapeHtml(l.pickLocation ?? "—")}</td>
      <td><span class="tick"></span></td>
    </tr>`,
    )
    .join("");
  return `<section class="order-doc">
    ${brandHeader(doc.identity)}
    <h1 class="doc-title">PICK SLIP</h1>
    <table class="info-panel">
      <tr>
        <th>Order</th><td>${escapeHtml(doc.orderNumber)}</td>
        <th>Order date</th><td>${escapeHtml(doc.orderDate)}</td>
      </tr>
      <tr>
        <th>Channel</th><td>${escapeHtml(doc.channel)}</td>
        <th>Customer</th><td>${escapeHtml(doc.customerName)}</td>
      </tr>
      <tr>
        <th>Customer / delivery phone</th><td class="phone-value">${escapeHtml(doc.customerPhone ?? "—")}</td>
        <th>Fulfilment location</th><td class="loc-cell">${escapeHtml(doc.fulfilmentLocation)}</td>
      </tr>
      <tr>
        <th>Deliver To</th><td class="deliver-cell" colspan="3">${postalHtml(doc.shipToPostal)}</td>
      </tr>
    </table>
    <table class="lines">
      <thead><tr>
        <th class="thumb">Image</th><th>Product</th><th>SKU</th><th>Barcode</th>
        <th class="num">Ordered</th><th class="num">Fulfilled</th><th class="num">To pick</th>
        <th>Pick location</th><th>Picked</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${notesBlock(doc)}
    ${sheetFooter(footerLeft)}
  </section>`;
}

function renderPack(doc: OrderDocumentModel, footerLeft: string): string {
  const rows = doc.lines
    .map(
      (l) => `<tr>
      ${thumbCell(l.imageUrl)}
      ${productCell(l)}
      <td class="mono wrap">${escapeHtml(l.sku)}</td>
      <td>${barcodePrintHtml(l.barcode, l.sku)}</td>
      <td class="num">${escapeHtml(l.qtyPacked)}</td>
      <td><span class="tick"></span></td>
    </tr>`,
    )
    .join("");
  return `<section class="order-doc">
    ${brandHeader(doc.identity)}
    <h1 class="doc-title">PACKING SLIP</h1>
    <table class="info-panel">
      <tr>
        <th>Order</th><td>${escapeHtml(doc.orderNumber)}</td>
        <th>Order date</th><td>${escapeHtml(doc.orderDate)}</td>
      </tr>
      <tr>
        <th>Channel</th><td>${escapeHtml(doc.channel)}</td>
        <th>Customer</th><td>${escapeHtml(doc.customerName)}</td>
      </tr>
      <tr>
        <th>Delivery phone</th><td class="phone-value">${escapeHtml(doc.customerPhone ?? "—")}</td>
        <th>Shipping method</th><td>${escapeHtml(doc.shippingMethod)}</td>
      </tr>
      <tr>
        <th>Ship To</th><td class="deliver-cell" colspan="3">${postalHtml(doc.shipToPostal)}</td>
      </tr>
    </table>
    <table class="lines">
      <thead><tr>
        <th class="thumb">Image</th><th>Product</th><th>SKU</th><th>Barcode</th>
        <th class="num">Quantity</th><th>Packed</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${notesBlock(doc)}
    ${sheetFooter(footerLeft)}
  </section>`;
}

function renderInvoice(doc: OrderDocumentModel, footerLeft: string): string {
  const m = doc.money;
  const cur = m.currency;
  const rows = doc.lines
    .map(
      (l) => `<tr>
      <td class="num">${escapeHtml(l.qtyOrdered)}</td>
      <td class="mono wrap">${escapeHtml(l.sku)}</td>
      <td class="wrap">${escapeHtml(l.productName)}${l.options ? `<div class="options">${escapeHtml(l.options)}</div>` : ""}</td>
      <td class="num">${escapeHtml(money(l.unitPrice, cur))}</td>
      <td class="num">${escapeHtml(money(l.lineTotal, cur))}</td>
    </tr>`,
    )
    .join("");
  return `<section class="order-doc">
    ${brandHeader(doc.identity)}
    <h1 class="doc-title">TAX INVOICE</h1>
    <table class="info-panel">
      <tr>
        <th>Invoice / order</th><td>${escapeHtml(doc.orderNumber)}</td>
        <th>Order date</th><td>${escapeHtml(doc.orderDate)}</td>
      </tr>
      <tr>
        <th>Payment</th><td>${escapeHtml(doc.payment)}</td>
        <th>Shipping method</th><td>${escapeHtml(doc.shippingMethod)}</td>
      </tr>
      <tr>
        <th>Currency</th><td>${escapeHtml(cur)}</td>
        <th>Customer</th><td>${escapeHtml(doc.customerName)}</td>
      </tr>
      <tr>
        <th>Bill To</th><td>${postalHtml(doc.billToPostal)}</td>
        <th>Ship To</th><td class="deliver-cell">${postalHtml(doc.shipToPostal)}</td>
      </tr>
    </table>
    <table class="lines">
      <thead><tr><th class="num">Qty</th><th>SKU</th><th>Product</th><th class="num">Unit price</th><th class="num">Line total</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totals">
      <div><span>Subtotal</span><span>${escapeHtml(money(m.subtotal, cur))}</span></div>
      ${m.discount ? `<div><span>Discount</span><span>−${escapeHtml(money(m.discount, cur))}</span></div>` : ""}
      <div><span>Shipping</span><span>${escapeHtml(money(m.shipping, cur))}</span></div>
      <div><span>${escapeHtml(m.taxCaption)}</span><span>${escapeHtml(money(m.tax, cur))}</span></div>
      <div class="grand"><span>Total</span><span>${escapeHtml(money(m.grandTotal, cur))}</span></div>
    </div>
    ${notesBlock(doc)}
    ${sheetFooter(footerLeft)}
  </section>`;
}

export function renderOrderDocumentsHtml(
  docs: OrderDocumentModel[],
  kind: OrderDocumentKind,
  title: string,
  options?: { printedAt?: Date },
): string {
  const printedAt = options?.printedAt ?? new Date();
  const footerLeft = `Printed ${formatPrintedAt(printedAt)} · ${kindTitle(kind)}`;
  const body = docs
    .map((d) => {
      if (kind === "pick") return renderPick(d, footerLeft);
      if (kind === "pack") return renderPack(d, footerLeft);
      return renderInvoice(d, footerLeft);
    })
    .join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>${printCss(kind, footerLeft)}</style></head><body>${body}</body></html>`;
}

export function openPrintWindow(html: string, title: string): void {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("title", title);
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;";
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  const doc = frame.contentDocument;
  if (!win || !doc) {
    frame.remove();
    throw new Error("Unable to open the print document.");
  }
  doc.open();
  doc.write(html);
  doc.close();
  doc.title = title;
  const cleanup = () => {
    if (frame.parentNode) frame.remove();
  };
  win.onafterprint = cleanup;
  const waitForImages = Array.from(doc.images).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  });
  void Promise.all(waitForImages).then(() => {
    win.focus();
    win.print();
    window.setTimeout(cleanup, 120000);
  });
}

export function assertTenantIsolation(docs: OrderDocumentModel[], orgId: string): OrderDocumentModel[] {
  return docs.filter((d) => d.orgId === orgId);
}
